import React, { useEffect, useState, useMemo } from "react";

import { LoadingIcon, InfoSmIcon, Typography } from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";

import ModalTrial from "Components/ModalTrial";
import Redirect from "Components/Redirect";
import {
  BACKUP_DESCRIPTIONS,
  BACKUP_TYPES,
  BACKUP_LABEL,
  GIG,
  STORAGE_SUFFIX
} from "Constants/constants";
import Button, { Link, IconControl } from "ds/Button";
import DialogGroupDefault, * as Dialog from "ds/Dialog";
import * as DropdownMenu from "ds/Dropdown";
import PageLoading from "ds/PageLoading";
import * as Sticker from "ds/Sticker";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import getUrl from "Libs/getUrl";
import logger from "Libs/logger";
import { capitalize, isDg3 } from "Libs/utils";
import { loadEnvironments } from "Reducers/environment";
import {
  selectUsedEnvironments,
  selectHasDedicatedDeployment
} from "Reducers/environment/environment.selectors";
import { selectFeatureAutomatedBackupsEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import {
  updateSubscription,
  getOrgSubscriptionEstimate,
  errorsSelector,
  subscriptionPlanOptionsSelector
} from "Reducers/organization/subscription";
import { gitProjectSelector } from "Reducers/project/project";
import useBlackFireSellableStatus from "src/organization/pages/project/containers/ProjectOverview/BlackfireCard/useBlackFireSellableStatus";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import AmountField from "./AmountField";
import Panel from "./BackupsCostPanel/BackupsCostPanel";
import BlackfireAddon from "./blackfireAddon/BlackfireAddon";
import PlanInfo from "./PlanInfo";
import * as S from "./styles";

const bigDevMap = {
  "": "Standard",
  PLATFORM_FEATURE_M_DEV: "Medium",
  PLATFORM_FEATURE_L_DEV: "Large",
  PLATFORM_FEATURE_XL_DEV: "Xlarge",
  PLATFORM_FEATURE_2XL_DEV: "2Xlarge",
  PLATFORM_FEATURE_4XL_DEV: "4Xlarge"
};

const generateDropdownScruct = valuesMap =>
  Object.entries(valuesMap).map(([key, value]) => ({
    value: key,
    label: value
  }));

const ProjectPlanForm = ({
  subscription,
  organizationNameId,
  organizationProfile,
  error,
  isOnTrial,
  isLoadingSubscription,
  currency
}) => {
  const automatedBackupsEnabled = useAppSelector(
    selectFeatureAutomatedBackupsEnabled
  );
  const [modalTrialOpen, openModalTrial] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [initEstimate, setInitEstimate] = useState();
  const [estimate, setEstimate] = useState();
  const [estimateSub, setEstimateSub] = useState();
  const [environmentOptions, setEnvironmentOptions] = useState([]);

  const { blackfireOrObservabilityIsAvailable, isEnterprise, isPurchased } =
    useBlackFireSellableStatus(subscription);

  const dedicatedDeployment = useSelectorWithUrlParams(
    selectHasDedicatedDeployment
  );

  const intl = useIntl();
  const dispatch = useAppDispatch();

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId: organizationNameId,
      projectId: subscription?.project_id
    })
  );
  const allEnvironments = useAppSelector(state =>
    selectUsedEnvironments(state, {
      organizationId: organizationNameId,
      projectId: subscription?.project_id
    })
  );

  const updateSizes = (planValue, environmentOptions) => {
    return environmentOptions?.[planValue] ?? [];
  };

  const updateEnvironmentOptionsHandler = sizes => {
    setEnvironmentOptions(
      sizes?.map(value => ({
        value,
        label: value
      }))
    );
  };

  useEffect(() => {
    if (project?.id) {
      dispatch(
        loadEnvironments({
          projectId: project.id,
          organizationId: organizationNameId
        })
      );
    }
  }, [dispatch, organizationNameId, project?.id]);

  useEffect(() => {
    if (
      (estimateSub?.plan && !environmentOptions?.length) ||
      subscription?.environment_options?.length
    ) {
      const sizes = updateSizes(
        estimateSub?.plan,
        subscription?.environment_options
      );
      updateEnvironmentOptionsHandler(sizes);
    }
  }, [
    estimateSub?.plan,
    environmentOptions?.length,
    subscription?.environment_options
  ]);

  const deletedSubscription = useAppSelector(
    state => state.organizationSubscription?.deleted
  );
  const bigDevTypes = useMemo(() => {
    const types = generateDropdownScruct(bigDevMap);
    // If the current Big Dev size for the subscription is Medium, show it in the options list.
    // Otherwise it should be filtered out because it can no longer  be added to a subscription.
    if (subscription.big_dev === "PLATFORM_FEATURE_M_DEV") {
      return types;
    }
    return types.filter(type => type.value !== "PLATFORM_FEATURE_M_DEV");
  }, [subscription]);

  const bigDevSelection = useMemo(() => {
    const devSize = subscription?.big_dev;

    if (estimateSub?.big_dev || estimateSub?.big_dev === "") {
      return bigDevTypes.find(type => type.value === estimateSub.big_dev);
    }

    return {
      value: devSize || bigDevTypes[0].value,
      label: bigDevMap[devSize] || bigDevTypes[0].label
    };
  }, [subscription?.big_dev, bigDevTypes, estimateSub?.big_dev]);

  const {
    backups,
    plan,
    support_tier,
    environments,
    storage,
    user_licenses,
    big_dev,
    project_options,
    id: subscriptionId,
    blackfire
  } = subscription;

  const isDg3Project = isDg3(subscription?.enterprise_tag, dedicatedDeployment);

  const automatedBackupsEnabledOrDg3 = automatedBackupsEnabled || isDg3Project;

  const defaultBackup = project_options?.features?.backups?.default[0];
  const availableBackup = project_options?.features?.backups?.available[0];

  const formatBackupDropdown = options =>
    options.map(key => {
      const backupLabel = BACKUP_TYPES[key]?.label || "";

      return {
        value: key,
        label: (
          <p>
            <S.Label textStyle="lowercaseControl">{backupLabel}</S.Label>
            <br />
            {intl.formatMessage(
              {
                id: `backups.dropdown.${backupLabel}.plan`,
                defaultMessage: BACKUP_DESCRIPTIONS[backupLabel]
              },
              {
                br: <br />
              }
            )}
          </p>
        ),
        selectedLabel: backupLabel
      };
    });

  // Display the purchased sellable if any; otherwise display default and sellable in dropdown
  const defaultBackupOption = defaultBackup ? [defaultBackup] : [];
  const currentBackupPlan = backups ? [backups] : defaultBackupOption;
  const sellableBackupOption = availableBackup ? [availableBackup] : [];

  let backupOptions = formatBackupDropdown(
    currentBackupPlan?.concat(sellableBackupOption)
  );

  if (
    backups === BACKUP_LABEL.BACKUPS_PREMIUM ||
    (backups === BACKUP_LABEL.BACKUPS_ADVANCED &&
      currentBackupPlan[0] === BACKUP_LABEL.BACKUPS_BASIC)
  ) {
    backupOptions = formatBackupDropdown(currentBackupPlan);
  }

  const initialBackup = backupOptions[0];

  const numberManual =
    BACKUP_TYPES[estimateSub?.backups || initialBackup?.value]?.manual || 2;
  const numberAutomated =
    BACKUP_TYPES[estimateSub?.backups || initialBackup?.value]?.automated || 2;

  const initEstimateSub = () => {
    setEstimateSub({
      backups,
      plan,
      environments,
      storage,
      user_licenses,
      big_dev,
      blackfire
    });
  };

  const planOptions = useAppSelector(state =>
    subscriptionPlanOptionsSelector(state, {
      organizationId: organizationNameId,
      subscriptionId
    })
  );

  useEffect(() => {
    if (subscription?.id) initEstimateSub();
  }, [subscription?.id]);

  useEffect(() => {
    let isCanceled = false;
    const getEstimation = async () => {
      const observabilitySuiteFeature = isEnterprise
        ? "observability_suite"
        : "blackfire";

      let observabilitySuiteFeatureValue = estimateSub?.blackfire || undefined;
      if (isEnterprise) {
        observabilitySuiteFeatureValue = isPurchased
          ? "PLATFORM_FEATURE_OBSERVABILITY_SUITE"
          : undefined;
      }

      const parameters = {
        plan: estimateSub?.plan,
        storage: estimateSub?.storage,
        environments: estimateSub?.environments,
        user_licenses: estimateSub?.user_licenses,
        big_dev: estimateSub?.big_dev ?? "",
        backups: estimateSub?.backups ?? "",
        format: "complex"
      };

      if (observabilitySuiteFeature) {
        parameters[observabilitySuiteFeature] = observabilitySuiteFeatureValue;
      }

      if (!isCanceled) {
        const result = await dispatch(
          getOrgSubscriptionEstimate({
            subscription,
            organizationId: organizationNameId,
            parameters
          })
        );

        if (result.payload?.options) {
          setEstimate(result.payload);
          if (!initEstimate) setInitEstimate(result.payload);
        }
      }
    };
    if (estimateSub) getEstimation();

    return () => {
      isCanceled = true;
    };
  }, [estimateSub]);

  const currentSubscriptionStorageGig = subscription?.storage / GIG;
  const storageOptions = useMemo(() => {
    const opts = [];
    for (let i = currentSubscriptionStorageGig; i <= 100; i += 5) {
      opts.push({
        value: i * GIG,
        label: `${i + STORAGE_SUFFIX}`
      });
    }

    return opts;
  }, [subscription?.storage]);

  const storageSelection =
    storageOptions.find(option => option.value === estimateSub?.storage) ??
    storageOptions[0];

  const EVENT_FIELDS = {
    plan: "plan",
    environments: "environments",
    bigDev: "big_dev",
    storage: "storage",
    backups: "backups"
  };

  const updateEnvironmentSizes = planValue => {
    const sizes = updateSizes(planValue, subscription?.environment_options);

    updateEnvironmentOptionsHandler(sizes);

    const acceptableEnvSize =
      sizes.find(size => size >= allEnvironments.length) ||
      sizes[sizes.length - 1];
    const subscriptionEnvironments = subscription?.environments;
    const included = sizes.includes(subscriptionEnvironments);
    return {
      environments: included ? subscriptionEnvironments : acceptableEnvSize
    };
  };

  const handleChange = (value, field) => {
    let newEnvironmentSize = {};
    // Only perform this calculation when the plan has changed
    if (field === EVENT_FIELDS.plan && estimateSub?.plan !== value) {
      newEnvironmentSize = updateEnvironmentSizes(value);
    }

    setEstimateSub(state => {
      return { ...state, [field]: value, ...newEnvironmentSize };
    });

    setIsChanged(true);
  };

  const resetChangedFields = () => {
    setIsChanged(false);
  };

  const handleCancel = () => {
    initEstimateSub();
    resetChangedFields();
  };

  const canUpdate = () => {
    if (!isOnTrial) return true;

    const current_trial = organizationProfile?.current_trial;
    const used = current_trial?.current.amount;
    const spend = current_trial?.spend.amount;
    const initCost = parseInt(initEstimate.total);
    const newCost = parseInt(estimate.total.formatted);
    return spend >= used - initCost + newCost;
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (!canUpdate()) {
      openModalTrial(true);
      return false;
    }
    dispatch(
      updateSubscription({
        updates: estimateSub,
        subscription,
        organizationId: organizationNameId
      })
    );
    if (!error) resetChangedFields();
  };

  const planLabel =
    planOptions.find(plan => plan?.value === estimateSub?.plan)?.label ?? "";

  const environmentOption = useMemo(() => {
    const option = environmentOptions?.find(
      env => env.value === estimateSub?.environments
    );
    return (
      option || {
        value: subscription?.environments,
        label: subscription?.environments
      }
    );
  }, [environmentOptions, estimateSub, subscription]);

  if (!estimate) return <PageLoading iconOnly />;

  const canEdit = subscription?.hasLink("update");
  const isNotProfessional = ["premier", "enterprise", "elite"].includes(
    support_tier
  );

  const isBackupPremium =
    backups === BACKUP_LABEL.BACKUPS_PREMIUM ||
    defaultBackup === BACKUP_LABEL.BACKUPS_PREMIUM;

  const isBackupBasic = defaultBackup === BACKUP_LABEL.BACKUPS_BASIC;

  const isBackupAdvancedAndWithNoSellable =
    (backups === BACKUP_LABEL.BACKUPS_ADVANCED ||
      defaultBackup === BACKUP_LABEL.BACKUPS_ADVANCED) &&
    availableBackup !== BACKUP_LABEL.BACKUPS_PREMIUM;

  const hasNoBackupOrDefault =
    !backups && !defaultBackup && !estimateSub?.backups;

  const backupsSelectedLabel = () => {
    const backupsLabel =
      BACKUP_TYPES[backups || estimateSub?.backups || defaultBackup]?.label;

    return backupsLabel || "Select";
  };

  const labelForSelectedBackup = backupsSelectedLabel();
  const storageInGB = estimateSub?.storage / GIG;
  const totalBackupsPrice = estimate?.backups?.formatted;
  const backupPricePerGig = estimate?.options?.backups?.formatted;
  // If "hasNoBackupOrDefault" then "totalBackupsPrice" will be 0
  const price =
    hasNoBackupOrDefault || isBackupBasic
      ? totalBackupsPrice
      : backupPricePerGig;
  const finalBackupPricePerGig = hasNoBackupOrDefault
    ? "0"
    : backupPricePerGig || totalBackupsPrice;

  const subscriptionEnvironments = subscription?.environments;

  return (
    <>
      <S.FormWrapper onSubmit={handleSubmit}>
        {deletedSubscription && (
          <Redirect
            to={getUrl({
              key: "organization.billing.plan",
              props: { organizationId: organizationNameId }
            })}
          />
        )}
        {subscription && (
          <>
            <S.Row>
              <S.Heading>
                <S.Info>
                  <S.PlanOption>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger
                        variant="picklist"
                        disabled={isOnTrial || !canEdit}
                      >
                        <S.TriggerWrapper>
                          <S.ValueText>{planLabel}</S.ValueText>
                          <S.TriggerChevron>
                            <DropdownMenu.Chevron />
                          </S.TriggerChevron>
                        </S.TriggerWrapper>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <S.ContentPlan align="end">
                          {planOptions?.map(({ label, value }) => {
                            return (
                              <S.Item
                                key={value}
                                $isSelected={estimateSub?.plan === value}
                                onSelect={() => {
                                  handleChange(value, EVENT_FIELDS.plan);
                                }}
                              >
                                {label}
                              </S.Item>
                            );
                          })}
                        </S.ContentPlan>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                    <AmountField>{estimate.plan.formatted}</AmountField>
                  </S.PlanOption>
                  <S.DescriptionLabelPlan>
                    {intl.formatMessage({
                      id: "project.plan.type",
                      defaultMessage: "Plan"
                    })}
                    {isOnTrial && (
                      <DialogGroupDefault
                        description={intl.formatMessage({
                          id: "project.plan.trial.info",
                          defaultMessage:
                            "Free trials are limited to development only plans, add your billing details to change your plan."
                        })}
                        link={
                          <Link
                            href={`/${organizationNameId}/-/billing`}
                            analyticId="project.plan.trial.info.link"
                          >
                            {intl.formatMessage({
                              id: "project.plan.trial.info.link",
                              defaultMessage: "Add billing details"
                            })}
                          </Link>
                        }
                        trigger={
                          <IconControl aria-label="info-trigger-add-billing">
                            <InfoSmIcon />
                          </IconControl>
                        }
                      />
                    )}
                  </S.DescriptionLabelPlan>
                </S.Info>
              </S.Heading>
              <S.Includes plan={estimateSub?.plan}>
                <S.DescriptionLabel>
                  {intl.formatMessage({
                    id: "project.plan.includes.title",
                    defaultMessage: "Includes"
                  })}
                </S.DescriptionLabel>
                <S.Features>
                  <PlanInfo plan={estimateSub?.plan} />
                </S.Features>
              </S.Includes>
              {estimateSub?.plan?.includes("high-memory") && (
                <Sticker.Root priority="information" variant="long">
                  <Sticker.Text>
                    <div>
                      {intl.formatMessage(
                        {
                          id: "project.plan.highmem_disclaimer"
                        },
                        {
                          b: chunks => <strong>{chunks}</strong>,
                          link_docs: chunks => (
                            <Link
                              analyticId="links.documentation.flexible_resources"
                              href={intl.formatMessage({
                                id: "links.documentation.flexible_resources"
                              })}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {chunks}
                            </Link>
                          ),
                          link_tix: chunks => (
                            <Link
                              href="/-/users/~/tickets/open"
                              target="_blank"
                              rel="noreferrer"
                              analyticId="tickets_open"
                            >
                              {chunks}
                            </Link>
                          )
                        }
                      )}
                    </div>
                  </Sticker.Text>
                </Sticker.Root>
              )}
            </S.Row>
            {blackfireOrObservabilityIsAvailable && (
              <S.Row>
                <BlackfireAddon
                  onProductChange={value => handleChange(value, "blackfire")}
                  blackfire={estimateSub?.blackfire}
                  subscription={subscription}
                  estimate={estimate}
                />
              </S.Row>
            )}
            <S.Row>
              <S.Heading>
                <S.Info>
                  <S.GeneralOption>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger
                        variant="picklist"
                        disabled={!canEdit}
                      >
                        <S.TriggerWrapper>
                          <S.ValueText>{environmentOption?.label}</S.ValueText>
                          <S.TriggerChevron>
                            <DropdownMenu.Chevron />
                          </S.TriggerChevron>
                        </S.TriggerWrapper>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <S.ContentEnvs align="end">
                          {environmentOptions?.map(({ label, value }) => {
                            return (
                              <S.Item
                                key={value}
                                $isSelected={environmentOption.value === value}
                                onSelect={() => {
                                  handleChange(
                                    value,
                                    EVENT_FIELDS.environments
                                  );
                                }}
                              >
                                {label}
                              </S.Item>
                            );
                          })}
                        </S.ContentEnvs>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                    <AmountField small={estimate.environments?.amount === 0}>
                      {estimate.environments?.amount === 0
                        ? "Included"
                        : estimate.environments?.formatted}
                    </AmountField>
                  </S.GeneralOption>
                  <S.DescriptionLabel>
                    {intl.formatMessage({
                      id: "project.plan.environment.title",
                      defaultMessage: "Environments"
                    })}
                  </S.DescriptionLabel>
                  <S.Price>
                    {intl.formatMessage(
                      {
                        id: "project.plan.environment.estimation",
                        defaultMessage: `${estimate?.options?.environments.formatted} / 3 environments`
                      },
                      { estimation: estimate?.options?.environments.formatted }
                    )}
                  </S.Price>
                </S.Info>
              </S.Heading>
              {subscriptionEnvironments !== environmentOption?.value && (
                <Sticker.Root priority="normal">
                  <Sticker.Icon priority="normal" />
                  <Sticker.Text>
                    {intl.formatMessage({
                      id: "plan_environment_confirmation"
                    })}
                  </Sticker.Text>
                </Sticker.Root>
              )}
            </S.Row>
            {/* Environments application size */}
            <S.Row>
              <S.Heading>
                <S.Info>
                  <S.MainLabelWrapper>
                    {intl.formatMessage({ id: "big_dev_title" })}
                  </S.MainLabelWrapper>
                  <S.AppSizeOption>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger
                        variant="picklist"
                        disabled={!canEdit}
                      >
                        <S.TriggerWrapper>
                          <S.ValueText>{bigDevSelection?.label}</S.ValueText>
                          <S.TriggerChevron>
                            <DropdownMenu.Chevron />
                          </S.TriggerChevron>
                        </S.TriggerWrapper>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <S.ContentBigDev align="end">
                          {bigDevTypes?.map(({ label, value }) => {
                            return (
                              <S.Item
                                key={value}
                                $isSelected={bigDevSelection?.value === value}
                                onSelect={() => {
                                  handleChange(value, EVENT_FIELDS.bigDev);
                                }}
                              >
                                {label}
                              </S.Item>
                            );
                          })}
                        </S.ContentBigDev>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                    <AmountField small={estimate.big_dev?.amount === 0}>
                      {estimate.big_dev?.amount === 0
                        ? "Included"
                        : estimate.big_dev?.formatted}
                    </AmountField>
                  </S.AppSizeOption>
                  <S.Price>
                    {intl.formatMessage({ id: "big_dev_desc" })}
                  </S.Price>
                  <S.DialogGroupDefault
                    description={intl.formatMessage({
                      id: "environment_application_size_message"
                    })}
                    analyticId="environment_application_size_message"
                    trigger={
                      <IconControl aria-label="info-trigger-environment">
                        <InfoSmIcon />
                      </IconControl>
                    }
                  />
                </S.Info>
              </S.Heading>
            </S.Row>
            {/* Storage */}
            <S.Row>
              <S.Heading>
                <S.Info>
                  <S.GeneralOption>
                    {storageOptions.length ? (
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger
                          variant="picklist"
                          disabled={!canEdit}
                        >
                          <S.TriggerWrapper>
                            <S.ValueText>{storageSelection?.label}</S.ValueText>
                            <S.TriggerChevron>
                              <DropdownMenu.Chevron />
                            </S.TriggerChevron>
                          </S.TriggerWrapper>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                          <S.ContentStorage align="end">
                            {storageOptions?.map(({ label, value }) => {
                              return (
                                <S.Item
                                  key={value}
                                  $isSelected={
                                    storageSelection?.value === value
                                  }
                                  onSelect={() => {
                                    handleChange(value, EVENT_FIELDS.storage);
                                  }}
                                >
                                  {label}
                                </S.Item>
                              );
                            })}
                          </S.ContentStorage>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    ) : (
                      <S.SubscriptionStorage>
                        {intl.formatMessage(
                          { id: "project.billing.value_gb" },
                          { value: currentSubscriptionStorageGig }
                        )}
                      </S.SubscriptionStorage>
                    )}
                    <AmountField small={estimate.storage.amount === 0}>
                      {estimate.storage.amount === 0
                        ? "Included"
                        : estimate.storage.formatted}{" "}
                      *
                    </AmountField>
                  </S.GeneralOption>
                  <S.DescriptionLabel>
                    {intl.formatMessage({
                      id: "project.plan.storage.title",
                      defaultMessage: "Storage"
                    })}
                  </S.DescriptionLabel>
                  <S.Price>
                    {intl.formatMessage(
                      {
                        id: "project.plan.storage.estimation",
                        defaultMessage: `${estimate?.options?.storage.formatted} / 5 GB per environment`
                      },
                      {
                        estimation: estimate?.options?.storage.formatted
                      }
                    )}
                  </S.Price>
                </S.Info>
              </S.Heading>
            </S.Row>
            {/* Backups -- section will be hidden behind feature flag for now */}
            {automatedBackupsEnabledOrDg3 &&
              !!sellableBackupOption?.length > 0 && (
                <S.Row>
                  <S.Heading>
                    <S.Info>
                      <S.BackupsOption>
                        {!isBackupPremium && (
                          <Panel
                            currency={currency}
                            hasNoBackupOrDefault={hasNoBackupOrDefault}
                            totalBackupsPrice={totalBackupsPrice}
                            storageInGB={storageInGB}
                            price={price}
                            finalBackupPricePerGig={finalBackupPricePerGig}
                          />
                        )}
                        {!isBackupPremium &&
                        !isBackupAdvancedAndWithNoSellable ? (
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger variant="picklist">
                              <S.TriggerWrapper>
                                <S.ValueText>
                                  {labelForSelectedBackup}
                                </S.ValueText>
                                <S.TriggerChevron>
                                  <DropdownMenu.Chevron />
                                </S.TriggerChevron>
                              </S.TriggerWrapper>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <S.ContentBackup align="end">
                                {backupOptions?.map(
                                  (
                                    { label, value, selectedLabel },
                                    _,
                                    originalBackupOptionsArr
                                  ) => {
                                    const handleValue =
                                      // If a user selects the backup that the project already has, don't send anything to the Backend
                                      originalBackupOptionsArr[0].value ===
                                      value
                                        ? undefined
                                        : value;

                                    return (
                                      <S.Item
                                        key={value}
                                        $isSelected={
                                          labelForSelectedBackup ===
                                          selectedLabel
                                        }
                                        onSelect={() => {
                                          handleChange(
                                            handleValue,
                                            EVENT_FIELDS.backups
                                          );
                                        }}
                                      >
                                        {label}
                                      </S.Item>
                                    );
                                  }
                                )}
                              </S.ContentBackup>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                        ) : (
                          <Typography.BodyText size="medium" weight="regular">
                            {intl.formatMessage({
                              id: `backups.dropdown.${
                                BACKUP_TYPES[estimateSub?.backups]?.label
                              }.label`,
                              defaultMessage:
                                BACKUP_TYPES[estimateSub?.backups]?.label
                            })}
                          </Typography.BodyText>
                        )}
                        <AmountField>
                          {estimate?.backups.formatted} *
                        </AmountField>
                      </S.BackupsOption>
                      <S.DescriptionLabel>
                        {intl.formatMessage({
                          id: "project.plan.backups.title",
                          defaultMessage: "Backups"
                        })}
                      </S.DescriptionLabel>
                      <S.Price>
                        {hasNoBackupOrDefault
                          ? intl.formatMessage({
                              id: "project.plan.backups.no-backup-no-default",
                              defaultMessage: `Upgrade to an automated backup plan for your production environment.`
                            })
                          : intl.formatMessage(
                              {
                                id: "project.plan.backups.estimation",
                                defaultMessage: `${numberAutomated} automated + ${numberManual} manual included`
                              },
                              {
                                numberAutomated,
                                numberManual
                              }
                            )}
                        {!isBackupPremium ? (
                          <Dialog.Root>
                            <Dialog.Trigger analyticId="project.plan.backups.tooltip.text">
                              <IconControl aria-label="info-trigger-backups">
                                <InfoSmIcon />
                              </IconControl>
                            </Dialog.Trigger>
                            <Dialog.Content>
                              <Dialog.Description>
                                {intl.formatMessage({
                                  id: "project.plan.backups.tooltip.text",
                                  defaultMessage:
                                    "Premium backup plans are available for Elite and Enterprise organizations."
                                })}
                              </Dialog.Description>
                              <S.LinkContainer>
                                <Link
                                  href={intl.formatMessage({
                                    id: "links.documentation.backups"
                                  })}
                                  analyticId="project.plan.backups.tooltip.url.text"
                                >
                                  {intl.formatMessage({
                                    id: "project.plan.backups.tooltip.url.text",
                                    defaultMessage: "Learn more"
                                  })}
                                </Link>{" "}
                                {support_tier !== "elite" && (
                                  <>
                                    |{" "}
                                    <Link
                                      analyticId="support_sla_link_text_contact"
                                      href={intl.formatMessage({
                                        id: "links.platform.contact"
                                      })}
                                    >
                                      {intl.formatMessage({
                                        id: "support_sla_link_text_contact",
                                        defaultMessage: "Contact"
                                      })}
                                    </Link>
                                  </>
                                )}
                              </S.LinkContainer>
                            </Dialog.Content>
                          </Dialog.Root>
                        ) : null}
                      </S.Price>
                    </S.Info>
                  </S.Heading>
                </S.Row>
              )}
            {/* Users */}
            <S.Row>
              <S.Heading>
                <S.Info>
                  <S.GeneralOption>
                    <S.UserLicenses>
                      {subscription.user_licenses}
                    </S.UserLicenses>
                    <AmountField
                      extraMargin={true}
                      small={estimate.user_licenses?.amount === 0}
                    >
                      {estimate.user_licenses?.amount === 0
                        ? "Included"
                        : estimate.user_licenses?.formatted}
                    </AmountField>
                  </S.GeneralOption>
                  <S.DescriptionLabel>
                    {intl.formatMessage({
                      id: "project.plan.users.title",
                      defaultMessage: "Users"
                    })}
                  </S.DescriptionLabel>
                  <S.Price>
                    {intl.formatMessage(
                      {
                        id: "project.plan.users.estimation",
                        defaultMessage: `${estimate?.options?.user_licenses.formatted} / per user per month. Billed when added to your platform`
                      },
                      {
                        estimation: estimate?.options?.user_licenses.formatted
                      }
                    )}
                  </S.Price>
                </S.Info>
              </S.Heading>
            </S.Row>
            {/* SLA */}
            <S.Row>
              <S.Heading>
                <S.Info>
                  <S.MainLabelWrapper>
                    {intl.formatMessage({
                      id: "project.plan.support.supportSLA",
                      defaultMessage: "Support SLA"
                    })}
                  </S.MainLabelWrapper>
                  <S.Options>
                    <S.ValueText>
                      {isNotProfessional
                        ? `${capitalize(support_tier)} ${intl.formatMessage({
                            id: "sla.support",
                            defaultMessage: "Support"
                          })}`
                        : `${intl.formatMessage({
                            id: "professional.support",
                            defaultMessage: "Professional support"
                          })}`}
                    </S.ValueText>
                    <AmountField extraMargin={true} small={true}>
                      Included
                    </AmountField>
                  </S.Options>

                  <S.Price>
                    {intl.formatMessage(
                      {
                        id: isNotProfessional
                          ? `support.sla.${support_tier}.text`
                          : `support.sla.professional.text`,
                        defaultMessage:
                          "Normal priority: 24 business hrs{br}High priority: 8 business hrs{br}Urgent priority: 1 hour, 24/7"
                      },
                      {
                        br: <br />
                      }
                    )}
                  </S.Price>
                  <Dialog.Root>
                    <Dialog.Trigger>
                      <IconControl aria-label="info-trigger-sla">
                        <InfoSmIcon />
                      </IconControl>
                    </Dialog.Trigger>
                    <Dialog.Content>
                      <Dialog.Description>
                        {intl.formatMessage({
                          id: "support_sla_explanation",
                          defaultMessage:
                            "A Support SLA can be added by contacting our sales team. They are inherited by the organization, but can in special cases be overridden by project."
                        })}
                      </Dialog.Description>
                      <S.LinkContainer>
                        <Link
                          href={intl.formatMessage({
                            id: "links.pricing.compare"
                          })}
                          analyticId="support_sla_link_text_learn"
                        >
                          {intl.formatMessage({
                            id: "support_sla_link_text_learn",
                            defaultMessage: "Learn more"
                          })}
                        </Link>{" "}
                        {support_tier !== "elite" && (
                          <>
                            |{" "}
                            <Link
                              analyticId="support_sla_link_text_contact"
                              href={intl.formatMessage({
                                id: "links.platform.contact"
                              })}
                            >
                              {intl.formatMessage({
                                id: "support_sla_link_text_contact",
                                defaultMessage: "Contact"
                              })}
                            </Link>
                          </>
                        )}
                      </S.LinkContainer>
                    </Dialog.Content>
                  </Dialog.Root>
                </S.Info>
              </S.Heading>
            </S.Row>

            {/* Origin traffic */}
            {isEnterprise && (
              <S.Row>
                <S.Heading>
                  <S.Info>
                    <S.MainLabelWrapper>
                      {intl.formatMessage({
                        id: "project.billing.origin_traffic"
                      })}
                    </S.MainLabelWrapper>
                    <S.Options>
                      <Typography.BodyText $color="granite">
                        {intl.formatMessage({
                          id: "project.billing.enterprise_only"
                        })}
                      </Typography.BodyText>
                      <AmountField extraMargin={true} small={true}>
                        {intl.formatMessage({
                          id: "project.billing.na"
                        })}{" "}
                        **
                      </AmountField>
                    </S.Options>

                    <S.Price>
                      {intl.formatMessage({ id: "bandwidth" })}:{" "}
                      {estimate?.egress_bandwidth?.current_usage_formatted}
                      <br />
                      {intl.formatMessage({ id: "http.requests" })}:{" "}
                      {estimate?.ingress_requests?.current_usage_formatted.replace(
                        " requests",
                        ""
                      )}
                    </S.Price>
                    <Dialog.Root>
                      <Dialog.Trigger>
                        <IconControl aria-label="info-trigger-usage">
                          <InfoSmIcon />
                        </IconControl>
                      </Dialog.Trigger>
                      <Dialog.Content>
                        <Dialog.Description>
                          {intl.formatMessage({
                            id: "project.billing.origin_traffic_note.long"
                          })}
                        </Dialog.Description>
                        <S.LinkContainer>
                          <Link
                            href={intl.formatMessage({
                              id: "links.pricing.compare"
                            })}
                          >
                            {intl.formatMessage({
                              id: "learn_more"
                            })}
                          </Link>
                        </S.LinkContainer>
                      </Dialog.Content>
                    </Dialog.Root>
                  </S.Info>
                </S.Heading>
              </S.Row>
            )}

            {/* CDN traffic */}
            {isEnterprise &&
              (estimate?.fastly_bandwidth || estimate?.fastly_requests) && (
                <S.Row>
                  <S.Heading>
                    <S.Info>
                      <S.MainLabelWrapper>
                        {intl.formatMessage(
                          {
                            id: "project.billing.fastly"
                          },
                          {
                            small: chunks => <small>{chunks}</small>
                          }
                        )}
                      </S.MainLabelWrapper>
                      <S.Options>
                        <Typography.BodyText $color="granite">
                          {intl.formatMessage({
                            id: "project.billing.enterprise_only"
                          })}
                        </Typography.BodyText>
                        <AmountField extraMargin={true} small={true}>
                          {intl.formatMessage({
                            id: "project.billing.na"
                          })}{" "}
                          **
                        </AmountField>
                      </S.Options>

                      <S.Price>
                        {intl.formatMessage({ id: "bandwidth" })}:{" "}
                        {estimate?.fastly_bandwidth?.current_usage_formatted}
                        <br />
                        {intl.formatMessage({ id: "http.requests" })}:{" "}
                        {estimate?.fastly_requests?.current_usage_formatted.replace(
                          " requests",
                          ""
                        )}
                      </S.Price>
                      <Dialog.Root>
                        <Dialog.Trigger>
                          <IconControl aria-label="info-trigger-usage">
                            <InfoSmIcon />
                          </IconControl>
                        </Dialog.Trigger>
                        <Dialog.Content>
                          <Dialog.Description>
                            {intl.formatMessage({
                              id: "project.billing.cdn_traffic_note.long"
                            })}
                          </Dialog.Description>
                          <S.LinkContainer>
                            <Link
                              href={intl.formatMessage({
                                id: "links.pricing.compare"
                              })}
                            >
                              {intl.formatMessage({
                                id: "learn_more"
                              })}
                            </Link>
                          </S.LinkContainer>
                        </Dialog.Content>
                      </Dialog.Root>
                    </S.Info>
                  </S.Heading>
                </S.Row>
              )}

            <S.TotalRow>
              <S.TotalHeading>
                <S.Options>
                  <S.TotalRowDescription>
                    {intl.formatMessage({
                      id: "total_monthly_cost",
                      defaultMessage: "Total monthly cost"
                    })}
                    <DialogGroupDefault
                      title={intl.formatMessage({
                        id: "estimated_cost",
                        defaultMessage: "Estimated cost"
                      })}
                      description={intl.formatMessage({
                        id: "billing_system_explanation",
                        defaultMessage:
                          "Billing is based on actual usage at the end of each month. Prices displayed include VAT when applicable."
                      })}
                      trigger={
                        <IconControl aria-label="info-trigger-estimated-cost">
                          <InfoSmIcon />
                        </IconControl>
                      }
                    />
                  </S.TotalRowDescription>
                  <S.Total>{estimate.total.formatted}</S.Total>
                </S.Options>
              </S.TotalHeading>
            </S.TotalRow>
            <>
              {isLoadingSubscription ? (
                <LoadingIcon />
              ) : (
                isChanged &&
                canEdit && (
                  <S.Footer>
                    <Button
                      variant="secondary"
                      analyticId="cancel"
                      onClick={handleCancel}
                    >
                      {intl.formatMessage({
                        id: "cancel",
                        defaultMessage: "Cancel"
                      })}
                    </Button>
                    <Button type="submit" analyticId="save">
                      {intl.formatMessage({
                        id: "save",
                        defaultMessage: "Save"
                      })}
                    </Button>
                  </S.Footer>
                )
              )}
            </>
          </>
        )}
        <ModalTrial
          isOpen={modalTrialOpen}
          organizationNameId={organizationNameId}
          closeModal={() => openModalTrial(false)}
          kind="plan"
        />
      </S.FormWrapper>
      <Typography.BodyText $color="slate">
        {intl.formatMessage({
          id: "project.plan.note.description"
        })}
        &nbsp;
        <Link
          analyticId="project.plan.note.text"
          to={`/-/users/${organizationNameId}/tickets/open?category=configuration_change&priority=low`}
        >
          {intl.formatMessage({
            id: "project.plan.note.text",
            defaultMessage: "support ticket"
          })}
        </Link>
        .
      </Typography.BodyText>
      {isEnterprise && (
        <Typography.BodyText $color="slate">
          {intl.formatMessage({
            id: "project.plan.overage_note"
          })}
        </Typography.BodyText>
      )}
    </>
  );
};

ProjectPlanForm.propTypes = {
  currency: PropTypes.string.isRequired,
  hasPaymentSource: PropTypes.bool,
  organizationNameId: PropTypes.string.isRequired,
  organizationProfile: PropTypes.object,
  error: PropTypes.string,
  isOnTrial: PropTypes.bool,
  subscription: PropTypes.object.isRequired,
  isLoadingSubscription: PropTypes.bool,
  tier: PropTypes.string
};

export default ProjectPlanForm;
